import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { YStack } from "tamagui";
import { useContactInviteContext } from "../components/contacts/ContactInviteContextWrapper";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import PhoneInput from "../components/shared/phone-input";
import TamaguiInput from "../components/shared/tamagui-input";
import { ThemedSwitch } from "../components/shared/themed-switch";
import Typography from "../components/shared/typography";
import {
  ContactsContextWrapper,
  useContactsContext,
} from "../hooks/ContactsContextWrapper";
import {
  UserContextWrapper,
  useUserContext,
} from "../hooks/UserContextWrapper";
import { useLoadContacts } from "../hooks/useLoadContacts";

export interface DependentAccountAccess {
  [key: string]: boolean;
}

export interface InviteUserForm {
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dependentAccountAccess: DependentAccountAccess;
}

function InviteUserPageBase() {
  const navigate = useNavigate();

  const { dependentAccounts } = useLoadContacts();
  const { selectedContact } = useContactsContext();
  const { inviteContact } = useContactInviteContext();
  const { update } = useUserContext();
  const [allOrNone, setAllOrNone] = useState(false);
  const contactRecord = selectedContact?.contact;
  const contactUser = contactRecord?.related_user;

  function getInitialValues() {
    // Should be set to true by default
    const dependentAccountAccess = dependentAccounts.reduce((acc, contact) => {
      const id = contact.related_user.id;
      if (id) {
        acc[id] = true;
      }
      return acc;
    }, {} as DependentAccountAccess);

    return {
      contactId: contactRecord?.id ?? "",
      firstName: contactUser?.first_name ?? "",
      lastName: contactUser?.last_name ?? "",
      email: contactUser?.email ?? "",
      phone: contactUser?.phone ?? "",
      dependentAccountAccess: { ...dependentAccountAccess },
    };
  }

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<InviteUserForm>({
    defaultValues: getInitialValues(),
    mode: "onChange",
  });

  const submit = async (data: InviteUserForm) => {
    const newEmail = data.email !== contactUser?.email && data.email;
    const newPhone = data.phone !== contactUser?.phone && data.phone;
    const newFirstName =
      data.firstName !== contactUser?.first_name && data.firstName;
    const newLastName =
      data.lastName !== contactUser?.last_name && data.lastName;

    if (newEmail || newPhone || newFirstName || newLastName) {
      await update({
        user: {
          id: contactUser?.id as string,
          ...(newEmail && { email: newEmail }),
          ...(newPhone && { phone: newPhone }),
          ...(newFirstName && { first_name: newFirstName }),
          ...(newLastName && { last_name: newLastName }),
        },
        contactId: contactRecord?.id,
      });
    }

    const inviteData = {
      relationshipId: contactRecord?.id as string,
      relatedAccountsAccessIds: Object.keys(data.dependentAccountAccess).filter(
        (id) => data.dependentAccountAccess[id] && id !== contactUser?.id
      ),
    };

    await inviteContact(inviteData);
    navigate(`/contacts`);
  };

  useEffect(() => {
    if (contactRecord) {
      setValue("contactId", contactRecord.id);
      setValue("firstName", contactUser?.first_name ?? "");
      setValue("lastName", contactUser?.last_name ?? "");
      setValue("email", contactUser?.email ?? "");
      setValue("phone", contactUser?.phone ?? "");
    }
  }, [contactRecord, contactUser, setValue]);

  const dependentValues = watch("dependentAccountAccess");

  useEffect(() => {
    if (!dependentValues) return;
    const hasAnyFalse = Object.values(dependentValues).some((v) => !v);

    setAllOrNone(!hasAnyFalse);
  }, [dependentValues]);

  function toggleAllOrNone(value: boolean) {
    setValue(
      "dependentAccountAccess",
      dependentAccounts.reduce((acc, contact) => {
        const id = contact.related_user.id;
        if (!id) return acc;
        return {
          ...acc,
          [id]: value,
        };
      }, {})
    );
  }

  useEffect(() => {
    setValue(
      "dependentAccountAccess",
      dependentAccounts.reduce((acc, contact) => {
        const id = contact.related_user.id;
        if (!id) return acc;
        return {
          ...acc,
          [id]: true,
        };
      }, {})
    );
  }, [dependentAccounts]);

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="w-full max-w-md mx-auto" variant="cream" elevation={2}>
        <Card.Header className="text-center" variant="cream">
          <Typography variant="h3" className="text-primary">
            Invite User
          </Typography>
        </Card.Header>
        <Card.Body>
          {errors.email?.message && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
              <Typography variant="body2">{errors.email.message}</Typography>
            </div>
          )}
          <form onSubmit={handleSubmit(submit)}>
            <YStack gap="$3">
              <TamaguiInput
                id="firstName"
                label="First Name"
                value={watch("firstName")}
                onChangeText={(value) => setValue("firstName", value)}
                placeholder="Enter first name"
                fullWidth
              />
              <TamaguiInput
                id="lastName"
                label="Last Name"
                value={watch("lastName")}
                onChangeText={(value) => setValue("lastName", value)}
                placeholder="Enter last name"
                fullWidth
              />
              <TamaguiInput
                id="email"
                label="Email"
                value={watch("email")}
                onChangeText={(value) => setValue("email", value)}
                placeholder="Enter email"
                fullWidth
              />
              <PhoneInput
                id="phone"
                label="Phone"
                value={watch("phone")}
                onChangeText={(value) => setValue("phone", value)}
                placeholder="Enter phone"
                fullWidth
              />
              {dependentAccounts.length > 0 && (
                <div className="flex flex-col gap-2">
                  <ThemedSwitch
                    name="allOrNone"
                    label="Dependent account access"
                    value={allOrNone}
                    onChange={toggleAllOrNone}
                    description={`This will grant ${contactUser?.first_name} access to all dependent accounts.`}
                  />
                  <div className="flex flex-col gap-2 ml-4">
                    {dependentAccounts
                      .filter((item) => {
                        const id = item.related_user.id;
                        return !!id && id !== contactUser?.id;
                      })
                      .map((contact) => {
                        const id = contact.related_user.id;
                        if (!id) return null;
                        return (
                          <ThemedSwitch
                            key={id}
                            name={id}
                            label={`${contact.related_user.first_name} ${contact.related_user.last_name} `}
                            value={Boolean(dependentValues?.[id])}
                            onChange={(value) =>
                              setValue("dependentAccountAccess", {
                                ...dependentValues,
                                [id]: value,
                              })
                            }
                          />
                        );
                      })}
                  </div>
                </div>
              )}
              <div className="pt-2">
                <SubmitButton
                  onClick={() => {}}
                  onSubmit={handleSubmit(submit)}
                  isDisabled={!watch("email") || !watch("phone")}
                  iconOnly={false}
                  size="md"
                  className="w-full"
                >
                  Send Invite
                </SubmitButton>
              </div>
              <div className="pt-2">
                <SubmitButton
                  type="button"
                  color="beige"
                  onClick={() => navigate(-1)}
                  iconOnly={false}
                  size="md"
                  className="w-full"
                  hideArrow={true}
                >
                  Cancel
                </SubmitButton>
              </div>
            </YStack>
          </form>
        </Card.Body>
      </Card>
    </div>
  );
}

export default function InviteUserPage() {
  const { relationshipId } = useParams();

  return (
    <ContactsContextWrapper relationshipId={relationshipId}>
      <UserContextWrapper>
        <InviteUserPageBase />
      </UserContextWrapper>
    </ContactsContextWrapper>
  );
}
